import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTelegram } from '../context/TelegramContext'
import Header from '../components/Header'
import './Categories.css'

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api').replace(/\/$/, '')

function Categories() {
  const navigate = useNavigate()
  const { isAdmin } = useTelegram()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [formData, setFormData] = useState({ name: '', emoji: '📦', display_order: 0 })

  useEffect(() => {
    if (isAdmin === false) {
      setError('Раздел доступен только администраторам')
      setLoading(false)
      return
    }

    if (isAdmin) {
      loadCategories()
    }
  }, [isAdmin])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/categories`)
      const data = await response.json()
      
      if (data.success) {
        setCategories(data.categories)
      } else {
        setError('Не удалось загрузить категории')
      }
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err)
      setError('Ошибка загрузки категорий')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const url = editingCategory
        ? `${API_URL}/categories/${editingCategory.id}`
        : `${API_URL}/categories`
      
      const method = editingCategory ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })
      
      const data = await response.json()
      
      if (data.success) {
        await loadCategories()
        handleCloseDialog()
      } else {
        alert(data.detail || 'Ошибка сохранения категории')
      }
    } catch (err) {
      console.error('Ошибка сохранения категории:', err)
      alert('Ошибка сохранения категории')
    }
  }

  const handleDelete = async (categoryId) => {
    if (!confirm('Удалить эту категорию? Это действие нельзя отменить.')) {
      return
    }
    
    try {
      const response = await fetch(`${API_URL}/categories/${categoryId}`, {
        method: 'DELETE',
      })
      
      const data = await response.json()
      
      if (data.success) {
        await loadCategories()
      } else {
        alert(data.detail || 'Ошибка удаления категории')
      }
    } catch (err) {
      console.error('Ошибка удаления категории:', err)
      alert('Ошибка удаления категории')
    }
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      emoji: category.emoji,
      display_order: category.display_order,
    })
    setShowAddDialog(true)
  }

  const handleAdd = () => {
    setEditingCategory(null)
    setFormData({ name: '', emoji: '📦', display_order: categories.length })
    setShowAddDialog(true)
  }

  const handleCloseDialog = () => {
    setShowAddDialog(false)
    setEditingCategory(null)
    setFormData({ name: '', emoji: '📦', display_order: 0 })
  }

  const handleMoveUp = async (category, index) => {
    if (index === 0) return
    
    const prevCategory = categories[index - 1]
    
    // Обновляем порядок обеих категорий
    await updateDisplayOrder(category.id, prevCategory.display_order)
    await updateDisplayOrder(prevCategory.id, category.display_order)
    
    await loadCategories()
  }

  const handleMoveDown = async (category, index) => {
    if (index === categories.length - 1) return
    
    const nextCategory = categories[index + 1]
    
    // Обновляем порядок обеих категорий
    await updateDisplayOrder(category.id, nextCategory.display_order)
    await updateDisplayOrder(nextCategory.id, category.display_order)
    
    await loadCategories()
  }

  const updateDisplayOrder = async (categoryId, newOrder) => {
    try {
      await fetch(`${API_URL}/categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ display_order: newOrder }),
      })
    } catch (err) {
      console.error('Ошибка обновления порядка:', err)
    }
  }

  const emojiList = ['📦', '👕', '🎀', '👟', '👜', '📱', '💄', '📚', '⚽', '🏠', '💍', '🎁', '🛍️', '📌', '🔖']

  if (loading) {
    return (
      <div className="categories-page">
        <Header title="Категории" showBack={true} />
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  if (isAdmin === false) {
    return (
      <div className="categories-page">
        <Header title="Категории" showBack={true} />
        <div className="permission-message">
          <p>Раздел доступен только администраторам.</p>
          <button className="btn-back-home" onClick={() => navigate('/')}>Вернуться в каталог</button>
        </div>
      </div>
    )
  }

  return (
    <div className="categories-page">
      <Header title="Категории" showBack={true} />
      
      <div className="categories-container">
        {error && <div className="error-message">{error}</div>}
        
        <div className="categories-actions">
          <button className="btn-add-category" onClick={handleAdd}>
            ➕ Добавить категорию
          </button>
        </div>

        <div className="categories-list">
          {categories.map((category, index) => (
            <div key={category.id} className="category-item">
              <div className="category-info">
                <span className="category-emoji">{category.emoji}</span>
                <span className="category-name">{category.name}</span>
              </div>
              
              <div className="category-actions">
                <button
                  className="btn-move"
                  onClick={() => handleMoveUp(category, index)}
                  disabled={index === 0}
                >
                  ↑
                </button>
                <button
                  className="btn-move"
                  onClick={() => handleMoveDown(category, index)}
                  disabled={index === categories.length - 1}
                >
                  ↓
                </button>
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(category)}
                >
                  ✏️
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(category.id)}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>

        {categories.length === 0 && !error && (
          <div className="empty-state">
            <p>Категорий пока нет</p>
            <button className="btn-add-first" onClick={handleAdd}>
              Создать первую категорию
            </button>
          </div>
        )}
      </div>

      {showAddDialog && (
        <div className="dialog-overlay" onClick={handleCloseDialog}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCategory ? 'Редактировать категорию' : 'Новая категория'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Название категории</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Например: Одежда"
                  required
                />
              </div>

              <div className="form-group">
                <label>Эмодзи</label>
                <div className="emoji-picker">
                  {emojiList.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className={`emoji-option ${formData.emoji === emoji ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, emoji })}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.emoji}
                  onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                  placeholder="Или введите свой эмодзи"
                  maxLength="2"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseDialog}>
                  Отмена
                </button>
                <button type="submit" className="btn-save">
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Categories



